import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import activeUserService, {
  ActiveUser,
  ActiveUsersStats,
  DailyActivity,
  DailyActivityHistoryEntry,
  DailyActivityHistoryResponse
} from '../services/activeUser.service';

export const useActiveUserTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Start tracking when component mounts
    activeUserService.startTracking();

    // Update activity when route changes
    activeUserService.updateUserActivity(location.pathname);

    // Cleanup when component unmounts
    return () => {
      activeUserService.stopTracking();
    };
  }, [location.pathname]);

  const updateActivity = useCallback((page?: string) => {
    activeUserService.updateUserActivity(page);
  }, []);

  return {
    updateActivity,
    isTracking: activeUserService.isTrackingActive()
  };
};

interface ActiveUsersPagination {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  count: number;
  role?: string;
}

export const useActiveUsers = () => {
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<ActiveUsersPagination>({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
    count: 0
  });
  const lastRequestRef = useRef<{ role?: string; page?: number; limit?: number }>({
    page: 1,
    limit: 10
  });

  const requestActiveUsers = useCallback(
    async (options: { role?: string; page?: number; limit?: number } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const role = options.role && options.role.toLowerCase() !== 'all' ? options.role : undefined;
        const params = {
          page: options.page,
          limit: options.limit
        };

        const response = role
          ? await activeUserService.getActiveUsersByRole(role, params)
          : await activeUserService.getActiveUsers(params);

        setActiveUsers(response.activeUsers);
        setPagination({
          page: response.page,
          totalPages: response.totalPages,
          total: response.total,
          limit: response.limit,
          count: response.count ?? response.activeUsers.length,
          role
        });
        lastRequestRef.current = {
          role,
          page: response.page,
          limit: response.limit
        };
      } catch (err: any) {
        setError(err.message || 'Failed to fetch active users');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchActiveUsers = useCallback(
    async (options: { page?: number; limit?: number } = {}) => {
      await requestActiveUsers(options);
    },
    [requestActiveUsers]
  );

  const fetchActiveUsersByRole = useCallback(
    async (role: string, options: { page?: number; limit?: number } = {}) => {
      await requestActiveUsers({ role, ...options });
    },
    [requestActiveUsers]
  );

  useEffect(() => {
    requestActiveUsers({ page: 1 });
  }, [requestActiveUsers]);

  return {
    activeUsers,
    loading,
    error,
    fetchActiveUsers,
    fetchActiveUsersByRole,
    pagination,
    refetch: () => requestActiveUsers(lastRequestRef.current)
  };
};

export const useActiveUserCount = () => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userCount = await activeUserService.getActiveUserCount();
      setCount(userCount);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch active user count');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    
    // Refresh count every 30 seconds
    const interval = setInterval(fetchCount, 30000);
    
    return () => clearInterval(interval);
  }, [fetchCount]);

  return {
    count,
    loading,
    error,
    refetch: fetchCount
  };
};

export const useActiveUsersStats = (options?: { enabled?: boolean }) => {
  const [stats, setStats] = useState<ActiveUsersStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enabled = options?.enabled ?? true;

  const fetchStats = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const statistics = await activeUserService.getActiveUsersStats();
      setStats(statistics);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch active users statistics');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setStats(null);
      setLoading(false);
      setError(null);
      return;
    }

    fetchStats();
    
    // Refresh stats every 60 seconds
    const interval = setInterval(fetchStats, 60000);
    
    return () => clearInterval(interval);
  }, [fetchStats, enabled]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  };
};

export const useUserActivityStatus = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [userActivity, setUserActivity] = useState<ActiveUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await activeUserService.getUserActivityStatus();
      setIsActive(status.isActive);
      setUserActivity(status.userActivity);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch user activity status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    isActive,
    userActivity,
    loading,
    error,
    refetch: fetchStatus
  };
};

export default useActiveUserTracking;

export const useDailyActivity = () => {
  const [activities, setActivities] = useState<DailyActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    totalPages: number;
    total: number;
    limit: number;
  }>({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  const lastRequestRef = useRef<{ date?: string; role?: string; page?: number; limit?: number }>({
    page: 1,
    limit: 10
  });

  const fetchDailyActivity = useCallback(
    async (params: { date?: string; role?: string; page?: number; limit?: number } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const response = await activeUserService.getDailyActivity(params);
        setActivities(response.activities);
        setPagination({
          page: response.page,
          totalPages: response.totalPages,
          total: response.total,
          limit: response.limit
        });
        lastRequestRef.current = {
          date: params.date,
          role: params.role,
          page: response.page,
          limit: response.limit
        };
      } catch (err: any) {
        setError(err.message || 'Failed to fetch daily activity');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    activities,
    loading,
    error,
    pagination,
    fetchDailyActivity,
    refetch: () => fetchDailyActivity(lastRequestRef.current)
  };
};

interface DailyActivityHistoryState {
  records: DailyActivityHistoryEntry[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
}

export const useDailyActivityHistory = () => {
  const [history, setHistory] = useState<DailyActivityHistoryState>({
    records: [],
    total: 0,
    page: 1,
    totalPages: 1,
    limit: 10
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(
    async (userId: string, options: { page?: number; limit?: number } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const response: DailyActivityHistoryResponse = await activeUserService.getDailyActivityHistory(
          userId,
          options
        );
        setHistory({
          records: response.records,
          total: response.total,
          page: response.page,
          totalPages: response.totalPages,
          limit: response.limit
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch activity history');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    history,
    loading,
    error,
    fetchHistory
  };
};
